import React, { useMemo, useState } from "react";
import { Link, useLoaderData } from "react-router-dom";
import { Button } from "primereact/button";
import { Chips } from "primereact/chips";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import { IsNullOrEmpty } from "../../../services/CustomService";
import {
    getAdminAlloVoisinsAutomation,
    getAdminAlloVoisinsSuggestions,
    replaceAdminAlloVoisinsTerms,
    updateAdminAlloVoisinsAlsoAvailable,
    updateAdminAlloVoisinsAutomationSettings
} from "../../../data/admin/allovoisinsAutomation";

export async function loader() {
    const automation = await getAdminAlloVoisinsAutomation("", "all", 50);
    return { automation };
}

const filterOptions = [
    { label: "Tous", value: "all" },
    { label: "Configurés", value: "configured" },
    { label: "Sans termes", value: "missing" },
    { label: "Collisions", value: "collisions" }
];

const messageTokens = [
    {
        token: "[NOM_OUTIL]",
        description: "Nom du matériel associé à la demande AlloVoisins."
    },
    {
        token: "[LIEN_OUTIL]",
        description: "Lien public Lokapp de la fiche matériel, si une URL publique existe."
    },
    {
        token: "[PRIX_JOUR]",
        description: "Prix de location par jour du matériel, en euros."
    },
    {
        token: "[MESSAGE_LIVRAISON]",
        description: "Phrase de livraison ajoutée seulement si le prix par jour atteint le seuil livraison."
    },
    {
        token: "[MARQUE_OUTIL]",
        description: "Marque renseignée sur la fiche matériel."
    },
    {
        token: "[NOM_MODELE]",
        description: "Modèle renseigné sur la fiche matériel."
    },
    {
        token: "[EGALEMENT_DISPONIBLE]",
        description: "Texte des autres matériels également disponibles, si renseigné sur la fiche."
    },
    {
        token: "[PRENOM]",
        description: "Prénom du propriétaire du matériel."
    },
    {
        token: "[VILLE]",
        description: "Ville du propriétaire du matériel."
    },
    {
        token: "[SEUIL_LIVRAISON]",
        description: "Montant du seuil livraison configuré dans ce panneau."
    }
];

function normalizeTool(tool, fallback = {}) {
    return {
        ...fallback,
        ...tool,
        toolId: tool?.toolId ?? tool?.ToolId ?? fallback.toolId,
        toolPublicId: tool?.toolPublicId ?? tool?.ToolPublicId ?? fallback.toolPublicId,
        toolCustomUrl: tool?.toolCustomUrl ?? tool?.ToolCustomUrl ?? fallback.toolCustomUrl,
        toolName: tool?.toolName ?? tool?.ToolName ?? fallback.toolName,
        brand: tool?.brand ?? tool?.Brand ?? fallback.brand,
        model: tool?.model ?? tool?.Model ?? fallback.model,
        category: tool?.category ?? tool?.Category ?? fallback.category,
        base64Picture: tool?.base64Picture ?? tool?.Base64Picture ?? fallback.base64Picture,
        priceDayCents: tool?.priceDayCents ?? tool?.PriceDayCents ?? fallback.priceDayCents ?? 0,
        ownerUserId: tool?.ownerUserId ?? tool?.OwnerUserId ?? fallback.ownerUserId,
        ownerFirstname: tool?.ownerFirstname ?? tool?.OwnerFirstname ?? fallback.ownerFirstname,
        ownerLastname: tool?.ownerLastname ?? tool?.OwnerLastname ?? fallback.ownerLastname,
        ownerEmail: tool?.ownerEmail ?? tool?.OwnerEmail ?? fallback.ownerEmail,
        alsoAvailableInformation: tool?.alsoAvailableInformation ?? tool?.AlsoAvailableInformation ?? fallback.alsoAvailableInformation ?? "",
        termCount: tool?.termCount ?? tool?.TermCount ?? fallback.termCount ?? 0,
        hasTermCollision: tool?.hasTermCollision ?? tool?.HasTermCollision ?? fallback.hasTermCollision ?? false,
        terms: (tool?.terms ?? tool?.Terms ?? fallback.terms ?? []).map(normalizeTerm),
        suggestions: tool?.suggestions ?? tool?.Suggestions ?? fallback.suggestions ?? [],
        recentRequests: tool?.recentRequests ?? tool?.RecentRequests ?? fallback.recentRequests ?? 0,
        recentRequestsByStatus: tool?.recentRequestsByStatus ?? tool?.RecentRequestsByStatus ?? fallback.recentRequestsByStatus ?? []
    };
}

function normalizeTerm(term) {
    return {
        id: term?.id ?? term?.Id,
        term: term?.term ?? term?.Term,
        normalizedTerm: term?.normalizedTerm ?? term?.NormalizedTerm,
        hasCollision: term?.hasCollision ?? term?.HasCollision ?? false,
        collisions: (term?.collisions ?? term?.Collisions ?? []).map(normalizeCollision)
    };
}

function normalizeCollision(collision) {
    return {
        toolId: collision?.toolId ?? collision?.ToolId,
        toolPublicId: collision?.toolPublicId ?? collision?.ToolPublicId,
        toolName: collision?.toolName ?? collision?.ToolName,
        ownerEmail: collision?.ownerEmail ?? collision?.OwnerEmail
    };
}

function normalizeAutomation(data) {
    return {
        ...(data ?? {}),
        generatedAtUtc: data?.generatedAtUtc ?? data?.GeneratedAtUtc,
        settings: normalizeSettings(data?.settings ?? data?.Settings),
        totalActiveTools: data?.totalActiveTools ?? data?.TotalActiveTools ?? 0,
        configuredTools: data?.configuredTools ?? data?.ConfiguredTools ?? 0,
        toolsWithoutTerms: data?.toolsWithoutTerms ?? data?.ToolsWithoutTerms ?? 0,
        totalTerms: data?.totalTerms ?? data?.TotalTerms ?? 0,
        duplicateTermGroups: data?.duplicateTermGroups ?? data?.DuplicateTermGroups ?? 0,
        recentRequests: data?.recentRequests ?? data?.RecentRequests ?? 0,
        recentRequestsByStatus: data?.recentRequestsByStatus ?? data?.RecentRequestsByStatus ?? [],
        tools: (data?.tools ?? data?.Tools ?? []).map((tool) => normalizeTool(tool))
    };
}

function normalizeSettings(settings) {
    return {
        hasAccount: settings?.hasAccount ?? settings?.HasAccount ?? false,
        customMessage: settings?.customMessage ?? settings?.CustomMessage ?? "",
        defaultCustomMessage: settings?.defaultCustomMessage ?? settings?.DefaultCustomMessage ?? "",
        minimumDeliveryPriceCents: settings?.minimumDeliveryPriceCents ?? settings?.MinimumDeliveryPriceCents ?? 3000
    };
}

function formatDateTime(value) {
    if (!value) {
        return "-";
    }

    return new Date(value).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });
}

function getOwnerName(tool) {
    return [tool.ownerFirstname, tool.ownerLastname].filter(Boolean).join(" ") || tool.ownerEmail || "-";
}

function uniqueTerms(terms) {
    const seen = new Set();
    return (terms ?? [])
        .map((term) => (term ?? "").trim().replace(/\s+/g, " "))
        .filter((term) => {
            const key = term.toLowerCase();
            if (!term || seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
}

function getCollisionGroups(terms) {
    const groups = new Map();

    (terms ?? []).filter((term) => term.hasCollision).forEach((term) => {
        const key = (term.normalizedTerm || term.term || "").trim().toLowerCase();
        if (!key) {
            return;
        }

        const group = groups.get(key) ?? {
            key,
            label: term.normalizedTerm || term.term,
            variants: [],
            collisions: []
        };

        if (term.term && !group.variants.some((variant) => variant.toLowerCase() === term.term.toLowerCase())) {
            group.variants.push(term.term);
        }

        term.collisions.forEach((collision) => {
            const collisionKey = collision.toolPublicId || collision.toolId || collision.toolName;
            if (!group.collisions.some((item) => (item.toolPublicId || item.toolId || item.toolName) === collisionKey)) {
                group.collisions.push(collision);
            }
        });

        groups.set(key, group);
    });

    return Array.from(groups.values());
}

function MetricCard({ label, value, tone }) {
    return (
        <article className={`admin-av-metric ${tone ? `admin-av-metric-${tone}` : ""}`}>
            <span>{label}</span>
            <strong>{Number(value ?? 0).toLocaleString("fr-FR")}</strong>
        </article>
    );
}

export default function AdminAlloVoisinsAutomationPage() {
    const { automation } = useLoaderData();
    const initialData = useMemo(() => normalizeAutomation(automation), [automation]);
    const [data, setData] = useState(initialData);
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState("all");
    const [take, setTake] = useState(50);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [selectedPublicId, setSelectedPublicId] = useState(data.tools[0]?.toolPublicId ?? null);
    const [termDraft, setTermDraft] = useState(data.tools[0]?.terms?.map((term) => term.term) ?? []);
    const [alsoAvailableDraft, setAlsoAvailableDraft] = useState(data.tools[0]?.alsoAvailableInformation ?? "");
    const [savingAlsoAvailable, setSavingAlsoAvailable] = useState(false);
    const [settingsDraft, setSettingsDraft] = useState(() => ({
        customMessage: initialData.settings.customMessage || initialData.settings.defaultCustomMessage || "",
        minimumDeliveryPriceCents: initialData.settings.minimumDeliveryPriceCents
    }));
    const [error, setError] = useState(null);

    const selectedTool = useMemo(
        () => data.tools.find((tool) => tool.toolPublicId === selectedPublicId) ?? data.tools[0] ?? null,
        [data.tools, selectedPublicId]
    );
    const selectedCollisionGroups = useMemo(
        () => getCollisionGroups(selectedTool?.terms),
        [selectedTool?.terms]
    );

    const setSelection = (tool) => {
        setSelectedPublicId(tool?.toolPublicId ?? null);
        setTermDraft(tool?.terms?.map((term) => term.term) ?? []);
        setAlsoAvailableDraft(tool?.alsoAvailableInformation ?? "");
        setError(null);
    };

    const refresh = async (nextQuery = query, nextFilter = filter, nextTake = take, preferredPublicId = selectedPublicId) => {
        setLoading(true);
        setError(null);
        try {
            const result = normalizeAutomation(await getAdminAlloVoisinsAutomation(nextQuery.trim(), nextFilter, nextTake));
            setData(result);
            setSettingsDraft({
                customMessage: result.settings.customMessage || result.settings.defaultCustomMessage || "",
                minimumDeliveryPriceCents: result.settings.minimumDeliveryPriceCents
            });
            const nextSelected = result.tools.find((tool) => tool.toolPublicId === preferredPublicId) ?? result.tools[0] ?? null;
            setSelection(nextSelected);
        } catch (err) {
            setError(err.message || "Impossible de charger la configuration AlloVoisins.");
        } finally {
            setLoading(false);
        }
    };

    const switchToCollisionTool = async (collision) => {
        if (!collision?.toolPublicId) {
            return;
        }

        const existingTool = data.tools.find((tool) => tool.toolPublicId === collision.toolPublicId);
        if (existingTool) {
            setSelection(existingTool);
            return;
        }

        setQuery(collision.toolPublicId);
        setFilter("all");
        await refresh(collision.toolPublicId, "all", take, collision.toolPublicId);
    };

    const saveTerms = async () => {
        if (!selectedTool) {
            return;
        }

        setSaving(true);
        setError(null);
        try {
            const updatedTool = normalizeTool(await replaceAdminAlloVoisinsTerms(selectedTool.toolPublicId, uniqueTerms(termDraft)));
            setData((prev) => ({
                ...prev,
                tools: prev.tools.map((tool) => tool.toolPublicId === updatedTool.toolPublicId ? updatedTool : tool)
            }));
            setSelection(updatedTool);
            await refresh(query, filter, take, updatedTool.toolPublicId);
        } catch (err) {
            setError(err.message || "Impossible d'enregistrer les termes.");
        } finally {
            setSaving(false);
        }
    };

    const saveAlsoAvailable = async () => {
        if (!selectedTool) {
            return;
        }

        setSavingAlsoAvailable(true);
        setError(null);
        try {
            const updatedTool = normalizeTool(await updateAdminAlloVoisinsAlsoAvailable(selectedTool.toolPublicId, alsoAvailableDraft), selectedTool);
            setData((prev) => ({
                ...prev,
                tools: prev.tools.map((tool) => tool.toolPublicId === updatedTool.toolPublicId ? updatedTool : tool)
            }));
            setAlsoAvailableDraft(updatedTool.alsoAvailableInformation ?? "");
        } catch (err) {
            setError(err.message || "Impossible d'enregistrer le texte « également disponible ».");
        } finally {
            setSavingAlsoAvailable(false);
        }
    };

    const saveSettings = async () => {
        setSavingSettings(true);
        setError(null);
        try {
            const customMessage = (settingsDraft.customMessage || "").trim();
            const defaultMessage = (data.settings.defaultCustomMessage || "").trim();
            const updatedSettings = normalizeSettings(await updateAdminAlloVoisinsAutomationSettings({
                customMessage: customMessage && customMessage !== defaultMessage ? settingsDraft.customMessage : null,
                minimumDeliveryPriceCents: settingsDraft.minimumDeliveryPriceCents
            }));
            setData((prev) => ({ ...prev, settings: updatedSettings }));
            setSettingsDraft({
                customMessage: updatedSettings.customMessage || updatedSettings.defaultCustomMessage || "",
                minimumDeliveryPriceCents: updatedSettings.minimumDeliveryPriceCents
            });
        } catch (err) {
            setError(err.message || "Impossible d'enregistrer les paramètres AlloVoisins.");
        } finally {
            setSavingSettings(false);
        }
    };

    const resetMessageTemplate = () => {
        setSettingsDraft((prev) => ({
            ...prev,
            customMessage: data.settings.defaultCustomMessage || ""
        }));
    };

    const addMessageToken = (token) => {
        setSettingsDraft((prev) => ({
            ...prev,
            customMessage: `${prev.customMessage || ""}${prev.customMessage ? " " : ""}${token}`
        }));
    };

    const addSuggestion = (suggestion) => {
        setTermDraft((prev) => uniqueTerms([...(prev ?? []), suggestion]));
    };

    const refreshSuggestions = async () => {
        if (!selectedTool) {
            return;
        }

        setLoading(true);
        try {
            const suggestions = await getAdminAlloVoisinsSuggestions(selectedTool.toolPublicId);
            const updatedTool = { ...selectedTool, suggestions: suggestions ?? [] };
            setData((prev) => ({
                ...prev,
                tools: prev.tools.map((tool) => tool.toolPublicId === updatedTool.toolPublicId ? updatedTool : tool)
            }));
        } catch (err) {
            setError(err.message || "Impossible de rafraîchir les suggestions.");
        } finally {
            setLoading(false);
        }
    };

    const toolBodyTemplate = (rowData) => (
        <div className="admin-av-tool-cell">
            {(!IsNullOrEmpty(rowData.base64Picture)) ? (
                <img className="smallPicture" alt={rowData.toolName} src={rowData.base64Picture || null} />
            ) : (
                <div className="no-picture no-picture-small"></div>
            )}
            <div>
                <div className="admin-av-tool-title">{rowData.toolName}</div>
                <small>{[rowData.brand, rowData.model].filter(Boolean).join(" ") || rowData.category || rowData.toolPublicId}</small>
            </div>
        </div>
    );

    const termsBodyTemplate = (rowData) => (
        <div className="admin-av-term-summary">
            <Tag value={`${rowData.termCount} terme${rowData.termCount > 1 ? "s" : ""}`} severity={rowData.termCount === 0 ? "warning" : "success"} />
            {rowData.hasTermCollision ? <Tag value="collision" severity="danger" /> : null}
        </div>
    );

    return (
        <div className="admin-av-page">
            <Tooltip target=".admin-av-token-help" position="top" />

            <div className="admin-av-header">
                <div>
                    <h1>Automatisation AlloVoisins</h1>
                    <p>Configuration des termes utilisés par Echelon pour reconnaître les demandes et contacter les prospects.</p>
                </div>
                <span>Mise à jour : {formatDateTime(data.generatedAtUtc)}</span>
            </div>

            {error ? <p className="admin-av-error">{error}</p> : null}

            <div className="admin-av-metrics">
                <MetricCard label="Outils actifs" value={data.totalActiveTools} />
                <MetricCard label="Configurés" value={data.configuredTools} tone="success" />
                <MetricCard label="Sans termes" value={data.toolsWithoutTerms} tone="warning" />
                <MetricCard label="Termes" value={data.totalTerms} />
                <MetricCard label="Collisions" value={data.duplicateTermGroups} tone={data.duplicateTermGroups > 0 ? "danger" : "success"} />
                <MetricCard label="Demandes 30j" value={data.recentRequests} />
            </div>

            <section className="admin-av-settings card">
                <div className="admin-av-settings-main">
                    <div className="admin-av-section-title">
                        <div>
                            <h2>Paramètres du message automatique</h2>
                            <p>Template utilisé par Echelon lorsqu'une demande AlloVoisins correspond à un outil.</p>
                        </div>
                        <Tag
                            value={data.settings.hasAccount ? "Compte configuré" : "Compte manquant"}
                            severity={data.settings.hasAccount ? "success" : "warning"}
                        />
                    </div>
                    <InputTextarea
                        value={settingsDraft.customMessage}
                        rows={5}
                        autoResize
                        onChange={(event) => setSettingsDraft((prev) => ({ ...prev, customMessage: event.target.value }))}
                    />
                    <div className="admin-av-token-list">
                        {messageTokens.map((variable) => (
                            <Button
                                key={variable.token}
                                type="button"
                                label={variable.token}
                                icon="pi pi-plus"
                                size="small"
                                outlined
                                className="admin-av-token-help"
                                data-pr-tooltip={variable.description}
                                onClick={() => addMessageToken(variable.token)}
                            />
                        ))}
                    </div>
                </div>
                <div className="admin-av-settings-side">
                    <label>
                        <span>Seuil livraison</span>
                        <InputNumber
                            value={(settingsDraft.minimumDeliveryPriceCents ?? 0) / 100}
                            min={0}
                            step={1}
                            mode="currency"
                            currency="EUR"
                            locale="fr-FR"
                            onValueChange={(event) => setSettingsDraft((prev) => ({
                                ...prev,
                                minimumDeliveryPriceCents: Math.round((event.value ?? 0) * 100)
                            }))}
                        />
                    </label>
                    <div className="admin-av-editor-actions">
                        <Button label="Enregistrer" icon="pi pi-save" loading={savingSettings} onClick={saveSettings} />
                        <Button label="Réinitialiser" icon="pi pi-undo" severity="secondary" outlined onClick={resetMessageTemplate} />
                    </div>
                </div>
            </section>

            <div className="admin-av-toolbar card">
                <InputText
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Rechercher outil, URL ou terme"
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            refresh();
                        }
                    }}
                />
                <Dropdown value={filter} options={filterOptions} onChange={(event) => setFilter(event.value)} />
                <InputNumber value={take} min={10} max={250} step={10} showButtons onValueChange={(event) => setTake(event.value ?? 50)} />
                <Button className="admin-av-search-button" label="Rechercher" icon="pi pi-search" loading={loading} onClick={() => refresh()} />
            </div>

            <div className="admin-av-layout">
                <section className="card admin-av-table-card">
                    <DataTable
                        className="admin-av-tools-table"
                        value={data.tools}
                        dataKey="toolPublicId"
                        paginator
                        rows={15}
                        showGridlines
                        selectionMode="single"
                        selection={selectedTool}
                        onSelectionChange={(event) => setSelection(event.value)}
                        emptyMessage="Aucun outil trouvé"
                    >
                        <Column header="Outil" body={toolBodyTemplate} sortable sortField="toolName" style={{ width: "76%" }} />
                        <Column header="AV" body={termsBodyTemplate} style={{ width: "24%" }} />
                    </DataTable>
                </section>

                <aside className="card admin-av-editor">
                    {selectedTool ? (
                        <>
                            <div className="admin-av-editor-header">
                                <div>
                                    <h2>{selectedTool.toolName}</h2>
                                    <p>{getOwnerName(selectedTool)}</p>
                                </div>
                                <Link to={selectedTool.toolCustomUrl ? `/outil/${selectedTool.toolCustomUrl}` : `/materiel/${selectedTool.toolPublicId}`}>
                                    Voir
                                </Link>
                            </div>

                            <div className="admin-av-editor-section">
                                <h3>Termes associés</h3>
                                <Chips
                                    value={termDraft}
                                    onChange={(event) => setTermDraft(uniqueTerms(event.value))}
                                    separator=","
                                    allowDuplicate={false}
                                    placeholder="Ajouter un terme puis Entrée"
                                />
                                <div className="admin-av-editor-actions">
                                    <Button label="Enregistrer" icon="pi pi-save" loading={saving} onClick={saveTerms} />
                                    <Button label="Annuler" icon="pi pi-undo" severity="secondary" outlined onClick={() => setSelection(selectedTool)} />
                                </div>
                            </div>

                            <div className="admin-av-editor-section">
                                <h3>Également disponible</h3>
                                <p className="admin-av-muted">Remplace [EGALEMENT_DISPONIBLE] dans le message envoyé pour cet outil.</p>
                                <InputTextarea
                                    value={alsoAvailableDraft}
                                    rows={3}
                                    autoResize
                                    placeholder="Ex. Je loue aussi une remorque et un taille-haie."
                                    onChange={(event) => setAlsoAvailableDraft(event.target.value)}
                                />
                                <div className="admin-av-editor-actions">
                                    <Button
                                        label="Enregistrer"
                                        icon="pi pi-save"
                                        loading={savingAlsoAvailable}
                                        disabled={(alsoAvailableDraft ?? "").trim() === (selectedTool.alsoAvailableInformation ?? "").trim()}
                                        onClick={saveAlsoAvailable}
                                    />
                                    <Button
                                        label="Annuler"
                                        icon="pi pi-undo"
                                        severity="secondary"
                                        outlined
                                        onClick={() => setAlsoAvailableDraft(selectedTool.alsoAvailableInformation ?? "")}
                                    />
                                </div>
                            </div>

                            <div className="admin-av-editor-section">
                                <div className="admin-av-section-title">
                                    <h3>Suggestions</h3>
                                    <Button icon="pi pi-refresh" text rounded aria-label="Rafraîchir les suggestions" loading={loading} onClick={refreshSuggestions} />
                                </div>
                                <div className="admin-av-suggestions">
                                    {(selectedTool.suggestions ?? []).map((suggestion) => (
                                        <Button
                                            key={suggestion}
                                            label={suggestion}
                                            icon="pi pi-plus"
                                            size="small"
                                            outlined
                                            onClick={() => addSuggestion(suggestion)}
                                        />
                                    ))}
                                </div>
                            </div>

                            <div className="admin-av-editor-section">
                                <h3>Collisions</h3>
                                {selectedCollisionGroups.length > 0 ? (
                                    <div className="admin-av-collisions">
                                        {selectedCollisionGroups.map((group) => (
                                            <div key={group.key} className="admin-av-collision">
                                                <div className="admin-av-collision-header">
                                                    <strong>{group.label}</strong>
                                                    <Tag value={`${group.collisions.length} outil${group.collisions.length > 1 ? "s" : ""}`} severity="danger" />
                                                </div>
                                                <span>Ce terme est déjà associé à :</span>
                                                <ul>
                                                    {group.collisions.map((collision) => (
                                                        <li key={collision.toolPublicId || collision.toolId || collision.toolName}>
                                                            <div className="admin-av-collision-tool-title">
                                                                <strong>{collision.toolName}</strong>
                                                                <Tag value={`ID ${collision.toolId}`} severity="danger" />
                                                            </div>
                                                            {collision.toolPublicId ? <small>PublicId : {collision.toolPublicId}</small> : null}
                                                            {collision.ownerEmail ? <small>{collision.ownerEmail}</small> : null}
                                                            <div className="admin-av-collision-actions">
                                                                <Button
                                                                    label="Basculer sur cet outil"
                                                                    icon="pi pi-arrow-right"
                                                                    size="small"
                                                                    outlined
                                                                    loading={loading}
                                                                    onClick={() => switchToCollisionTool(collision)}
                                                                />
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                                {group.variants.length > 1 ? (
                                                    <small className="admin-av-muted">
                                                        Variantes sur cet outil : {group.variants.join(", ")}
                                                    </small>
                                                ) : null}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="admin-av-muted">Aucune collision détectée pour cet outil.</p>
                                )}
                            </div>

                            <div className="admin-av-editor-section">
                                <h3>Demandes récentes de cet outil</h3>
                                <div className="admin-av-statuses">
                                    {(selectedTool.recentRequestsByStatus ?? []).length > 0 ? selectedTool.recentRequestsByStatus.map((status) => (
                                        <Tag key={status.status} value={`${status.status} : ${status.count}`} />
                                    )) : <span className="admin-av-muted">Aucune demande sur 30 jours.</span>}
                                </div>
                            </div>
                        </>
                    ) : (
                        <p className="admin-av-muted">Sélectionnez un outil pour administrer ses termes.</p>
                    )}
                </aside>
            </div>
        </div>
    );
}
